// @ts-nocheck
"use client"

import { useEffect, useMemo, useState } from "react"
import { ScrollArea } from "@/components/ui/scroll-area"
import { AnimatePresence, motion } from "motion/react"
import { ArrowCircleLeft, ArrowCircleRight, ArrowDownward, ArrowUpward } from "@mui/icons-material"
import {
  parseAllVariableTags,
  type ContractVariable,
  type ContractVariableMap,
  type GetContractResponseContent,
} from "src/types/contract.types"

interface CitationsPanelProps {
  contract?: GetContractResponseContent
  highlightedTerm?: string | null
  onTermSelect?: (termWithInstance: string) => void
  taggedContractText?: string
}

interface CitationTermData extends ContractVariable {
  title: string
  definition: string
  citation: string
}

const filterNumberedDuplicates = (variables: ContractVariableMap): ContractVariableMap => {
  const filtered: ContractVariableMap = {}

  Object.entries(variables).forEach(([id, variable]) => {
    if (/.*_\d+$/.test(id)) {
      const baseId = id.replace(/_\d+$/, "")
      if (!variables[baseId]) {
        filtered[id] = variable
      }
      return
    }

    filtered[id] = variable
  })

  return filtered
}

const slideVariants = {
  enter: (direction: number) => ({
    y: direction > 0 ? "24px" : "-24px",
    opacity: 0,
  }),
  center: {
    y: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    y: direction > 0 ? "-24px" : "24px",
    opacity: 0,
  }),
}

const getTermAndInstance = (termWithInstance?: string | null) => {
  if (!termWithInstance) {
    return { termId: null, instance: 1 }
  }

  if (!termWithInstance.includes(":")) {
    return { termId: termWithInstance, instance: 1 }
  }

  const [termId, instanceRaw] = termWithInstance.split(":")
  const parsedInstance = Number.parseInt(instanceRaw, 10)

  return {
    termId: termId || null,
    instance: Number.isFinite(parsedInstance) && parsedInstance > 0 ? parsedInstance : 1,
  }
}

export function CitationsPanel({
  contract,
  highlightedTerm = null,
  onTermSelect,
  taggedContractText,
}: CitationsPanelProps) {
  const allTerms = useMemo(() => {
    if (!contract?.contractExtraction?.variables) {
      return [] as CitationTermData[]
    }

    const filteredVariables = filterNumberedDuplicates(contract.contractExtraction.variables)
    return Object.values(filteredVariables)
      .sort(
        (a, b) =>
          (a.firstOccurrence ?? Number.MAX_SAFE_INTEGER) - (b.firstOccurrence ?? Number.MAX_SAFE_INTEGER)
      )
      .map((variable) => ({
        ...variable,
        title: variable.name,
        definition: variable.value || "No definition available.",
        citation:
          variable.definitionCitation ||
          variable.referencedSection ||
          variable.context ||
          `${variable.type} term`,
      }))
  }, [contract?.contractExtraction?.variables])

  const termInstanceCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    const contractText = taggedContractText ?? contract?.contractExtraction?.contractTexts?.taggedText?.text ?? ""

    if (!contractText) return counts

    const tags = parseAllVariableTags(contractText, { preserveInstance: true })
    tags.forEach((tag) => {
      const termId = tag.attributes.id
      if (!termId) return
      counts[termId] = (counts[termId] || 0) + 1
    })

    return counts
  }, [taggedContractText, contract?.contractExtraction?.contractTexts?.taggedText?.text])

  const [currentItem, setCurrentItem] = useState(0)
  const [currentInstance, setCurrentInstance] = useState(1)
  const [direction, setDirection] = useState<1 | -1>(1)

  useEffect(() => {
    if (allTerms.length === 0) {
      setCurrentItem(0)
      setCurrentInstance(1)
      return
    }

    setCurrentItem((previous) => Math.min(previous, allTerms.length - 1))
  }, [allTerms.length])

  const currentTerm = allTerms[currentItem]
  const instanceCount = currentTerm ? termInstanceCounts[currentTerm.id] || 1 : 1

  useEffect(() => {
    setCurrentInstance((previous) => Math.min(previous, instanceCount))
  }, [instanceCount])

  useEffect(() => {
    if (!highlightedTerm || allTerms.length === 0) return

    const { termId, instance } = getTermAndInstance(highlightedTerm)
    if (!termId) return

    const termIndex = allTerms.findIndex((term) => term.id === termId)
    if (termIndex === -1) return

    setDirection(1)
    setCurrentItem(termIndex)
    setCurrentInstance(Math.min(instance, termInstanceCounts[termId] || 1))
  }, [highlightedTerm, allTerms, termInstanceCounts])

  const syncSelection = (termIndex: number, instance: number) => {
    const term = allTerms[termIndex]
    if (!term) return
    onTermSelect?.(`${term.id}:${instance}`)
  }

  const handleNextTerm = () => {
    const nextIndex = Math.min(currentItem + 1, allTerms.length - 1)
    setDirection(1)
    setCurrentItem(nextIndex)
    setCurrentInstance(1)
    syncSelection(nextIndex, 1)
  }

  const handlePreviousTerm = () => {
    const previousIndex = Math.max(currentItem - 1, 0)
    setDirection(-1)
    setCurrentItem(previousIndex)
    setCurrentInstance(1)
    syncSelection(previousIndex, 1)
  }

  const handleNextInstance = () => {
    const nextInstance = Math.min(currentInstance + 1, instanceCount)
    setCurrentInstance(nextInstance)
    syncSelection(currentItem, nextInstance)
  }

  const handlePreviousInstance = () => {
    const previousInstance = Math.max(currentInstance - 1, 1)
    setCurrentInstance(previousInstance)
    syncSelection(currentItem, previousInstance)
  }

  if (!contract) {
    return (
      <ScrollArea className="flex-1">
        <div className="px-4 py-4">
          <p className="text-sm text-muted-foreground">No contract data available for citations.</p>
        </div>
      </ScrollArea>
    )
  }

  if (allTerms.length === 0) {
    return (
      <ScrollArea className="flex-1">
        <div className="px-4 py-4">
          <p className="text-sm text-muted-foreground">No citations found in this contract.</p>
        </div>
      </ScrollArea>
    )
  }

  return (
    <ScrollArea className="flex-1">
      <div className="px-4 py-4">
        <div className="p-4 bg-neutral-800 text-white border border-white/10 shadow-md rounded-2xl overflow-hidden">
          <h2 className="text-base font-medium">Citation</h2>
          <div className="h-px bg-white/10 my-4" />

          {instanceCount > 1 && (
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="disabled:opacity-50 hover:bg-white/10 rounded-full p-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                  onClick={handlePreviousInstance}
                  disabled={currentInstance === 1}
                  aria-label="Previous reference instance"
                  title="Previous reference instance"
                >
                  <ArrowUpward className="size-6" />
                </button>
                <button
                  type="button"
                  className="disabled:opacity-50 hover:bg-white/10 rounded-full p-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                  onClick={handleNextInstance}
                  disabled={currentInstance === instanceCount}
                  aria-label="Next reference instance"
                  title="Next reference instance"
                >
                  <ArrowDownward className="size-6" />
                </button>
              </div>
              <div className="text-white/60 text-sm">
                Reference {currentInstance} / {instanceCount}
              </div>
            </div>
          )}

          <AnimatePresence mode="popLayout" initial={false} custom={direction}>
            <motion.div
              key={currentTerm?.id || "citation-term"}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
              className="space-y-3"
            >
              {currentTerm && (
                <>
                  <p className="text-sm text-white/60">{currentTerm.citation}</p>
                  <h3 className="text-lg">{currentTerm.title}</h3>
                  <p className="text-xs uppercase tracking-wide text-white/50">Definition</p>
                  <p className="text-sm text-white/70 leading-relaxed">{currentTerm.definition}</p>
                </>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="h-px bg-white/10 my-4" />

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="disabled:opacity-50 hover:bg-white/10 rounded-full p-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                onClick={handlePreviousTerm}
                disabled={currentItem === 0}
                aria-label="Previous citation term"
                title="Previous citation term"
              >
                <ArrowCircleLeft className="size-8" />
              </button>
              <button
                type="button"
                className="disabled:opacity-50 hover:bg-white/10 rounded-full p-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                onClick={handleNextTerm}
                disabled={currentItem === allTerms.length - 1}
                aria-label="Next citation term"
                title="Next citation term"
              >
                <ArrowCircleRight className="size-8" />
              </button>
            </div>
            <div className="text-white/60 text-sm">
              Term {currentItem + 1} / {allTerms.length}
            </div>
          </div>
        </div>
      </div>
    </ScrollArea>
  )
}
